/**
 * Maersk API Token Service
 *
 * Obtains a bearer token via the local proxy endpoint /maersk-api/token
 * to avoid CORS issues and client certificate requirements.
 *
 * The token is cached in memory and reused until 30 s before expiry.
 */

// Use proxy endpoint instead of direct external API call
const TOKEN_PROXY_URL = "/maersk-api/token";

const OAUTH_CONSUMER_KEY = process.env.REACT_APP_POSTMORTEM_CONSUMER_KEY;

// consumer-key sent on every sre-pe-postmortem API call
const API_CONSUMER_KEY = OAUTH_CONSUMER_KEY;

let cachedToken    = null;
let tokenExpiresAt = 0; // epoch ms

// Helper function for retried fetch requests
const fetchWithRetryHelper = async (url, options = {}, retries = 3, delay = 1000) => {
  try {
    const response = await fetch(url, options);
    if (!response.ok && response.status >= 500) {
      throw new Error(`Server error: ${response.status}`);
    }
    return response;
  } catch (error) {
    const isRetryable = !error.message || error.message.includes('Server error') || error instanceof TypeError;
    if (retries > 0 && isRetryable) {
      await new Promise((res) => setTimeout(res, delay));
      return fetchWithRetryHelper(url, options, retries - 1, delay * 2);
    }
    throw error;
  }
};

/**
 * Returns a valid Maersk API bearer token, fetching a new one if
 * the cached token is absent or about to expire.
 */
export const getApiToken = async () => {
  const now = Date.now();

  if (cachedToken && now < tokenExpiresAt - 30_000) {
    return cachedToken;
  }

  // Call the local proxy endpoint which handles OAuth server-side
  // This avoids client certificate authentication and CORS issues
  const response = await fetchWithRetryHelper(TOKEN_PROXY_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
  }, 3, 1000);

  if (!response.ok) {
    const detail = await response.text().catch(() => "");
    throw new Error(
      `Maersk token request failed: ${response.status} ${response.statusText} — ${detail}`
    );
  }

  const data      = await response.json();
  cachedToken     = data.access_token;
  const expiresIn = data.expires_in ?? 3600; // seconds
  tokenExpiresAt  = now + expiresIn * 1000;

  return cachedToken;
};

/** Returns the consumer-key header value for sre-pe-postmortem API calls. */
export const getConsumerKey = () => API_CONSUMER_KEY;

/** Clears the in-memory token cache (useful for testing). */
export const clearTokenCache = () => {
  cachedToken = null;
  tokenExpiresAt = 0;
};
